import React, { Fragment, useEffect, useState } from "react";
import LinksComponent from "../../../components/Tenant/LinksComponent/LinksComponent";
import DataTableComponent from "../../../components/Tenant/DataTableComponent/DataTableComponent";
import { Menu, Transition } from "@headlessui/react";
import { useNavigate } from "react-router-dom";
import {
  FiEye, FiEdit2, FiCopy, FiPlus, FiSearch, FiCalendar,
  FiMoreVertical, FiUsers, FiLayers, FiAward, FiX,
  FiToggleLeft, FiToggleRight,
} from "react-icons/fi";
import { HiStar } from "react-icons/hi";
import { toast } from "react-hot-toast";
import { hasPermission } from "../../../utils/hasPermission";
import TenantSlugs from "../../../utils/TenantSlug";
import {
  useFetchAllSchemeMutation,
  useUpdateSchemeByIdMutation,
} from "../../../features/tenant/scheme/schemeApi";
import { useFetchAllCataloguesMutation } from "../../../features/tenant/giftCatalogue/giftCatalogueApi";
import { useCalculateTDSOfSchemeMutation } from "../../../features/tenant/schemeWallet/schemeWalletApi";
import ImageModal from "../../../components/Tenant/ImageModal/ImageModal";
import BulkRewardModal from "./BulkRewardModal";
import BulkManualQrRewardModal from "./BulkManualQrRewardModal";
import AddSchemeUsersModal from "./AddSchemeUsersModal";
import ViewSchemeUsersModal from "./ViewSchemeUsersModal";
import ViewSchemeModal from "./ViewSchemeModal";

// ── helpers ────────────────────────────────────────────────────────────────────

function fmtDate(raw) {
  if (!raw) return "—";
  const d = (raw || "").split("T")[0];
  const [y, m, day] = (d || "").split("-");
  return y && m && day ? `${day}/${m}/${y}` : "—";
}

// status: "1" = active, "2" = inactive, "0" = deleted
function statusLabel(status) {
  const s = String(status);
  if (s === "1") return "active";
  if (s === "2") return "inactive";
  if (s === "0") return "deleted";
  return "unknown";
}

// date-derived state for additional context
function dateState(row) {
  const now = new Date();
  const start = row.scheme_start ? new Date(row.scheme_start) : null;
  const end = row.scheme_end ? new Date(row.scheme_end) : null;
  if (start && now < start) return "upcoming";
  if (end && now > end) return "expired";
  return null;
}

const SCHEME_TYPE_NAMES = {
  1: "All Products",
  2: "Classification",
  3: "Category-Subcategory",
};

const STATUS_STYLES = {
  active:   { cls: "bg-green-50 text-green-700 border-green-200", dot: "bg-green-500"  },
  inactive: { cls: "bg-gray-100 text-gray-500 border-gray-200",   dot: "bg-gray-400"   },
  deleted:  { cls: "bg-red-50 text-red-600 border-red-200",       dot: "bg-red-400"    },
  unknown:  { cls: "bg-gray-100 text-gray-400 border-gray-200",   dot: "bg-gray-300"   },
};

// ── sub-components ─────────────────────────────────────────────────────────────

function StatusChip({ row }) {
  const s = statusLabel(row.status);
  const { cls, dot } = STATUS_STYLES[s] || STATUS_STYLES.unknown;
  const ds = dateState(row); // upcoming / expired / null
  return (
    <div className="flex flex-col gap-0.5">
      <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded text-[11px] font-semibold border capitalize ${cls}`}>
        <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${dot}`} />
        {s}
      </span>
      {ds && (
        <span className="text-[10px] text-gray-400 pl-0.5 capitalize">{ds}</span>
      )}
    </div>
  );
}

function TypeChip({ type }) {
  const label = SCHEME_TYPE_NAMES[Number(type)] || `Type ${type}`;
  return (
    <span className="inline-flex items-center px-2 py-0.5 rounded text-[11px] font-medium border bg-indigo-50 text-indigo-700 border-indigo-200">
      {label}
    </span>
  );
}

function ActionBtn({ onClick, title, icon: Icon, className }) {
  return (
    <button
      type="button"
      onClick={onClick}
      title={title}
      className={`inline-flex items-center gap-1 px-2 py-1 rounded text-[11px] font-medium border transition-colors ${className}`}
    >
      <Icon className="h-3.5 w-3.5 shrink-0" />
      {title}
    </button>
  );
}

function DatePeriod({ start, end }) {
  return (
    <span className="text-xs text-gray-500 font-mono whitespace-nowrap">
      {fmtDate(start)}
      <span className="mx-1 text-gray-300">→</span>
      {end ? fmtDate(end) : <span className="text-gray-400 not-italic">ongoing</span>}
    </span>
  );
}

// ── Status filter pills ────────────────────────────────────────────────────────

// "0" as string is truthy → passes controller's if(value) check; deleted also needs all:"true"
const STATUS_TABS = ["All", "active", "inactive", "deleted"];

function StatusTabs({ value, onChange }) {
  return (
    <div className="flex items-center gap-1 flex-wrap">
      {STATUS_TABS.map((s) => (
        <button
          key={s}
          type="button"
          onClick={() => onChange(s)}
          className={`px-3 py-1.5 rounded text-xs font-medium border transition-colors capitalize ${
            value === s
              ? "bg-gray-900 text-white border-gray-900"
              : "bg-white text-gray-600 border-gray-200 hover:border-gray-400 hover:text-gray-800"
          }`}
        >
          {s}
        </button>
      ))}
    </div>
  );
}

// Syncs Headless UI Menu open state to parent without hooks-in-render-props
function MenuOpenSync({ open, rowId, setActiveMenuRowId }) {
  useEffect(() => {
    setActiveMenuRowId((prev) => (open ? rowId : prev === rowId ? null : prev));
  }, [open, rowId, setActiveMenuRowId]);
  return null;
}

// ── Main ───────────────────────────────────────────────────────────────────────

const SchemeList = () => {
  const navigate = useNavigate();

  const addSchemePermission = hasPermission("addScheme");
  const editSchemePermission = hasPermission("editScheme");
  const updateSchemeStatusPermission = hasPermission("updateSchemeStatus");
  const viewSchemeDetailsPermission = hasPermission("viewSchemeDetails");
  const bulkBonusSchemePointsPermission = hasPermission("bulkBonusSchemePoints");
  const bulkManualQrRewardPermission = hasPermission("bulkManualQrReward");

  const tenant_id = JSON.parse(sessionStorage.getItem("vendorData") || "{}").tenant_id;
  const isSeedworks = String(tenant_id) === String(TenantSlugs.seedworks);
  const isLancer = String(tenant_id) === String(TenantSlugs.lancer);

  const primaryColor =
    JSON.parse(sessionStorage.getItem("vendorTheme") || "{}")?.theme?.color_shades || {};

  // ── list state ──
  const [activeMenuRowId, setActiveMenuRowId] = useState(null);
  const [data, setData] = useState([]);
  const [totalRows, setTotalRows] = useState(0);
  const [limit, setLimit] = useState(10);
  const [offset, setOffset] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [paginationResetToggle, setPaginationResetToggle] = useState(false);

  // ── filter state ──
  const [searchName, setSearchName] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [statusFilter, setStatusFilter] = useState("All");


  // ── modal state ──
  const [showModal, setShowModal] = useState(false);
  const [modalData, setModalData] = useState(null);
  const [showImageModal, setShowImageModal] = useState(false);
  const [showBulkRewardModal, setShowBulkRewardModal] = useState(false);
  const [showBulkManualQrRewardModal, setShowBulkManualQrRewardModal] = useState(false);
  const [showAddSchemeUsersModal, setShowAddSchemeUsersModal] = useState(false);
  const [selectedScheme, setSelectedScheme] = useState(null);
  const [selectedSchemeForQr, setSelectedSchemeForQr] = useState(null);
  const [viewUsersScheme, setViewUsersScheme] = useState(null);
  const [giftCataloguesList, setGiftCataloguesList] = useState([]);

  // ── API hooks ──
  const [fetchAllScheme, { isLoading: schemeIsLoading }] = useFetchAllSchemeMutation();
  const [fetchCatalogues, { data: cataloguesData }] = useFetchAllCataloguesMutation();
  const [updateScheme] = useUpdateSchemeByIdMutation();
  const [calculateSchemeTDS, { isLoading: calculatingTDSLoading }] = useCalculateTDSOfSchemeMutation();

  const auth = {
    token: sessionStorage.getItem("access_token"),
    tenant_id,
  };

  // status sent to API maps to scheme.status (LIKE filter in model)
  // deleted needs all:"true" to override the base whereIn("status",["1","2"])
  const STATUS_API = {
    active:   { status: "1" },
    inactive: { status: "2" },
    deleted:  { status: "0", all: "true" },
  };

  // ── fetch ──
  const fetchAllSchemeFunc = (overrides = {}) => {
    const statusParams = STATUS_API[statusFilter] || {};
    fetchAllScheme({
      ...auth,
      body: {
        name: searchName || undefined,
        startDate: startDate || undefined,
        endDate: endDate || undefined,
        limit,
        offset,
        ...statusParams,
        ...(isLancer && !statusParams.all ? { all: "true" } : {}),
        ...overrides,
      },
    }).then((res) => {
      const body = res?.data?.body;
      if (body) {
        setData(body.data || []);
        setTotalRows(Number(body.count) || 0);
      }
    });
  };

  useEffect(() => {
    fetchAllSchemeFunc();
  }, [limit, offset, statusFilter]); // eslint-disable-line

  useEffect(() => {
    fetchCatalogues(auth);
  }, []); // eslint-disable-line

  useEffect(() => {
    if (cataloguesData?.body) {
      const arr = [...cataloguesData.body];
      arr.sort((a, b) => a.catalogue_id - b.catalogue_id);
      setGiftCataloguesList(arr);
    }
  }, [cataloguesData]);

  const handleSearch = () => fetchAllSchemeFunc();

  const handleClearFilters = () => {
    setSearchName("");
    setStartDate("");
    setEndDate("");
    setStatusFilter("All");
    // fetchAllSchemeFunc will re-run via useEffect when statusFilter changes
  };

  const handleStatusChange = (row, newStatus) => {
    const isActivate = newStatus === "1";
    const label = isActivate ? "Activate" : "Deactivate";
    const btnClass = isActivate
      ? "px-3 py-1.5 rounded bg-emerald-600 text-white text-xs font-medium hover:bg-emerald-700"
      : "px-3 py-1.5 rounded bg-rose-600 text-white text-xs font-medium hover:bg-rose-700";

    toast(
      (t) => (
        <span onClick={(e) => e.stopPropagation()}>
          <p className="font-medium text-sm text-gray-800">
            {label} <span className="font-semibold">"{row.name}"</span>?
          </p>
          <div className="flex gap-2 mt-3">
            <button
              className={btnClass}
              onClick={async () => {
                toast.dismiss(t.id);
                try {
                  await updateScheme({ ...auth, id: row.id, body: { status: newStatus } }).unwrap();
                  toast.success(`Scheme ${label.toLowerCase()}d successfully`);
                  fetchAllSchemeFunc();
                } catch (err) {
                  toast.error(err?.data?.message || `Failed to ${label.toLowerCase()} scheme`);
                }
              }}
            >
              Yes, {label}
            </button>
            <button
              className="px-3 py-1.5 rounded border border-gray-300 text-xs font-medium hover:bg-gray-50"
              onClick={() => toast.dismiss(t.id)}
            >
              Cancel
            </button>
          </div>
        </span>
      ),
      { id: "scheme-status", duration: 7000 },
    );
  };

  const handlePageChange = (pageNumber) => {
    const newOffset = (pageNumber - 1) * limit;
    setOffset(newOffset);
    setCurrentPage(pageNumber);
  };

  const handlePerRowsChange = (newPerPage) => {
    setLimit(newPerPage);
    setOffset(0);
    setCurrentPage(1);
    setPaginationResetToggle((p) => !p);
  };

  // Server does the filtering; client filter is a safety-net fallback
  const filteredData = statusFilter === "All"
    ? data
    : data.filter((row) => String(row.status) === (STATUS_API[statusFilter]?.status || ""));

  // ── action handlers ──
  const handleBulkRewardClick = (row) => {
    const isInactive = new Date(row?.scheme_end) < new Date();
    toast(
      (t) => (
        <span onClick={(e) => e.stopPropagation()}>
          <p className="font-medium">
            {isInactive
              ? `Scheme ended on ${fmtDate(row?.scheme_end)}. Proceed with Bulk Reward?`
              : `Proceed with Bulk Reward for "${row.name}"?`}
          </p>
          <div className="flex gap-2 mt-3">
            <button
              className="px-3 py-1.5 rounded bg-blue-600 text-white text-xs font-medium hover:bg-blue-700"
              onClick={() => { setSelectedScheme(row); setShowBulkRewardModal(true); toast.dismiss(t.id); }}
            >
              Yes, proceed
            </button>
            <button
              className="px-3 py-1.5 rounded border border-gray-300 text-xs font-medium hover:bg-gray-50"
              onClick={() => toast.dismiss(t.id)}
            >
              Cancel
            </button>
          </div>
        </span>
      ),
      { id: "bulkReward", duration: 7000 },
    );
  };

  const handleBulkManualQrRewardClick = (row) => {
    const isInactive = new Date(row?.scheme_end) < new Date();
    toast(
      (t) => (
        <span onClick={(e) => e.stopPropagation()}>
          <p className="font-medium">
            {isInactive
              ? `Scheme ended on ${fmtDate(row?.scheme_end)}. Proceed with Manual QR Reward?`
              : `Proceed with Manual QR Reward for "${row.name}"?`}
          </p>
          <div className="flex gap-2 mt-3">
            <button
              className="px-3 py-1.5 rounded bg-blue-600 text-white text-xs font-medium hover:bg-blue-700"
              onClick={() => { setSelectedSchemeForQr(row); setShowBulkManualQrRewardModal(true); toast.dismiss(t.id); }}
            >
              Yes, proceed
            </button>
            <button
              className="px-3 py-1.5 rounded border border-gray-300 text-xs font-medium hover:bg-gray-50"
              onClick={() => toast.dismiss(t.id)}
            >
              Cancel
            </button>
          </div>
        </span>
      ),
      { id: "manualQrReward", duration: 7000 },
    );
  };

  const handleCalculateTDS = (row) => {
    if (calculatingTDSLoading) return;
    toast(
      (t) => (
        <span onClick={(e) => e.stopPropagation()}>
          <p className="font-medium">Calculate TDS for "{row.name}"?</p>
          <p className="text-xs text-red-600 mt-0.5">This action is irreversible.</p>
          <div className="flex gap-2 mt-3">
            <button
              className="px-3 py-1.5 rounded bg-red-600 text-white text-xs font-medium hover:bg-red-700"
              onClick={async () => {
                const res = await calculateSchemeTDS({ ...auth, scheme_id: row.id }).unwrap();
                res?.success ? toast.success("TDS calculated") : toast.error("Server error");
                toast.dismiss(t.id);
              }}
            >
              Calculate
            </button>
            <button
              className="px-3 py-1.5 rounded border border-gray-300 text-xs font-medium hover:bg-gray-50"
              onClick={() => toast.dismiss(t.id)}
            >
              Cancel
            </button>
          </div>
        </span>
      ),
      { id: "calculateTDS", duration: 7000 },
    );
  };

  // ── columns ───────────────────────────────────────────────────────────────────

  const columns = [
    {
      name: "#",
      cell: (_, idx) => (
        <span className="text-xs text-gray-400 font-mono">{offset + idx + 1}</span>
      ),
      width: "52px",
    },
    {
      name: "Name",
      cell: (row) => (
        <div className="py-1 min-w-0">
          <div className="flex items-center gap-1.5 flex-wrap">
            <span className="text-sm font-medium text-gray-900 truncate">{row.name}</span>
            {row.is_primary && (
              <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[10px] font-semibold bg-amber-50 text-amber-700 border border-amber-200 shrink-0">
                <HiStar className="w-2.5 h-2.5 fill-amber-400" />Primary
              </span>
            )}
          </div>
          {row.created_by_name && (
            <p className="text-[11px] text-gray-400 mt-0.5">by {row.created_by_name}</p>
          )}
        </div>
      ),
      sortable: true,
      minWidth: "180px",
    },
    {
      name: "Status",
      cell: (row) => <StatusChip row={row} />,
      width: "100px",
    },
    {
      name: "Type",
      cell: (row) => <TypeChip type={row.type} />,
      width: "160px",
    },
    {
      name: "Scheme Period",
      cell: (row) => <DatePeriod start={row.scheme_start} end={row.scheme_end} />,
      width: "200px",
    },
    {
      name: "Redeem Period",
      cell: (row) => <DatePeriod start={row.redeem_start} end={row.redeem_end} />,
      width: "200px",
    },
    {
      name: "User Check",
      cell: (row) => (
        row?.settings?.type === "scheme_id_check" ? (
          <button
            type="button"
            className="inline-flex items-center gap-1 px-2 py-1 rounded text-[11px] font-medium border bg-indigo-50 text-indigo-700 border-indigo-200 hover:bg-indigo-100 transition-colors"
            onClick={() => setViewUsersScheme({ id: row.id, name: row.name })}
          >
            <FiUsers className="h-3.5 w-3.5" />
            View Users
          </button>
        ) : (
          <span className="text-xs text-gray-400">Open</span>
        )
      ),
      width: "120px",
    },
    {
      name: "Actions",
      cell: (row) => (
        <div className="flex items-center gap-1 py-1 pb-3">
          {/* Primary inline actions */}
          <ActionBtn
            onClick={() => { setModalData(row); setShowModal(true); }}
            title="View"
            icon={FiEye}
            className="bg-gray-50 text-gray-600 border-gray-200 hover:bg-gray-100"
          />
          {editSchemePermission && (
            <ActionBtn
              onClick={() => navigate(`updateScheme`, { state: row })}
              title="Edit"
              icon={FiEdit2}
              className="bg-blue-50 text-blue-700 border-blue-200 hover:bg-blue-100"
            />
          )}
          {addSchemePermission && (
            <ActionBtn
              onClick={() => navigate(`create`, { state: { cloneFrom: row } })}
              title="Clone"
              icon={FiCopy}
              className="bg-indigo-50 text-indigo-700 border-indigo-200 hover:bg-indigo-100"
            />
          )}
          {updateSchemeStatusPermission && String(row.status) === "2" && (
            <ActionBtn
              onClick={() => handleStatusChange(row, "1")}
              title="Activate"
              icon={FiToggleRight}
              className="bg-green-50 text-green-700 border-green-200 hover:bg-green-100"
            />
          )}
          {updateSchemeStatusPermission && String(row.status) === "1" && (
            <ActionBtn
              onClick={() => handleStatusChange(row, "2")}
              title="Deactivate"
              icon={FiToggleLeft}
              className="bg-rose-50 text-rose-600 border-rose-200 hover:bg-rose-100"
            />
          )}

          {/* Overflow menu for secondary actions */}
          <Menu as="div" className="relative">
            {({ open }) => (
            <>
              <MenuOpenSync open={open} rowId={row.id} setActiveMenuRowId={setActiveMenuRowId} />
              <Menu.Button
                className="inline-flex items-center justify-center w-7 h-7 rounded border border-gray-200 bg-white text-gray-500 hover:bg-gray-50 hover:text-gray-700 transition-colors"
                title="More actions"
              >
                <FiMoreVertical className="h-3.5 w-3.5" />
              </Menu.Button>
            <Transition
              as={Fragment}
              enter="transition ease-out duration-100"
              enterFrom="transform opacity-0 scale-95"
              enterTo="transform opacity-100 scale-100"
              leave="transition ease-in duration-75"
              leaveFrom="transform opacity-100 scale-100"
              leaveTo="transform opacity-0 scale-95"
            >
              <Menu.Items className="absolute right-0 z-50 mt-1 w-48 rounded-lg bg-white shadow-lg ring-1 ring-gray-200 focus:outline-none overflow-hidden">
                <div className="py-1">
                  {viewSchemeDetailsPermission && (
                    <Menu.Item>
                      {({ active }) => (
                        <button
                          onClick={() => navigate(`details/${row.id}`, { state: row })}
                          className={`flex w-full items-center gap-2 px-3 py-2 text-xs font-medium ${active ? "bg-gray-50 text-gray-900" : "text-gray-700"}`}
                        >
                          <FiLayers className="h-3.5 w-3.5 shrink-0 text-gray-400" />
                          Details
                        </button>
                      )}
                    </Menu.Item>
                  )}
                  {viewSchemeDetailsPermission && (
                    <Menu.Item>
                      {({ active }) => (
                        <button
                          onClick={() => navigate(`wallets/${row.id}`, { state: row })}
                          className={`flex w-full items-center gap-2 px-3 py-2 text-xs font-medium ${active ? "bg-gray-50 text-gray-900" : "text-gray-700"}`}
                        >
                          <FiAward className="h-3.5 w-3.5 shrink-0 text-gray-400" />
                          Scheme Wallets
                        </button>
                      )}
                    </Menu.Item>
                  )}
                  {bulkBonusSchemePointsPermission && (
                    <Menu.Item>
                      {({ active }) => (
                        <button
                          onClick={() => handleBulkRewardClick(row)}
                          className={`flex w-full items-center gap-2 px-3 py-2 text-xs font-medium ${active ? "bg-gray-50 text-gray-900" : "text-gray-700"}`}
                        >
                          <FiAward className="h-3.5 w-3.5 shrink-0 text-gray-400" />
                          Bulk Reward
                        </button>
                      )}
                    </Menu.Item>
                  )}
                  {bulkManualQrRewardPermission && (
                    <Menu.Item>
                      {({ active }) => (
                        <button
                          onClick={() => handleBulkManualQrRewardClick(row)}
                          className={`flex w-full items-center gap-2 px-3 py-2 text-xs font-medium ${active ? "bg-gray-50 text-gray-900" : "text-gray-700"}`}
                        >
                          <FiLayers className="h-3.5 w-3.5 shrink-0 text-gray-400" />
                          Manual QR Reward
                        </button>
                      )}
                    </Menu.Item>
                  )}
                  {isSeedworks && (
                    <Menu.Item>
                      {({ active }) => (
                        <button
                          onClick={() => navigate(`tier/${row?.id}`, { state: row })}
                          className={`flex w-full items-center gap-2 px-3 py-2 text-xs font-medium ${active ? "bg-gray-50 text-gray-900" : "text-gray-700"}`}
                        >
                          <FiLayers className="h-3.5 w-3.5 shrink-0 text-gray-400" />
                          Manage Tiers
                        </button>
                      )}
                    </Menu.Item>
                  )}
                  {row?.settings?.tds_calc === true && new Date(row?.scheme_end) < new Date() && (
                    <>
                      <div className="border-t border-gray-100 my-1" />
                      <Menu.Item>
                        {({ active }) => (
                          <button
                            onClick={() => handleCalculateTDS(row)}
                            className={`flex w-full items-center gap-2 px-3 py-2 text-xs font-medium ${active ? "bg-amber-50 text-amber-800" : "text-amber-700"}`}
                          >
                            {calculatingTDSLoading ? "Calculating…" : "Calculate TDS"}
                          </button>
                        )}
                      </Menu.Item>
                    </>
                  )}
                </div>
              </Menu.Items>
            </Transition>
            </>
            )}
          </Menu>
        </div>
      ),
      minWidth: "240px",
    },
  ];

  // ── render ────────────────────────────────────────────────────────────────────

  return (
    <>
      <div className="flex w-full bg-gray-100 min-h-screen">
        <div className="w-full">

          {/* ── Page Header ── */}
          <div className="bg-white border-b border-gray-200 shadow-sm px-6 py-3 flex items-center justify-between">
            <div>
              <h1 className="text-base font-semibold text-gray-900">Schemes</h1>
              <LinksComponent />
            </div>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => navigate("view-stats")}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium border border-gray-200 bg-white text-gray-700 hover:bg-gray-50 transition-colors"
              >
                View Stats
              </button>
              {addSchemePermission && (
                <button
                  type="button"
                  onClick={() => setShowAddSchemeUsersModal(true)}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium border border-gray-200 bg-white text-gray-700 hover:bg-gray-50 transition-colors"
                >
                  <FiUsers className="h-3.5 w-3.5" />
                  Add Users
                </button>
              )}
              {addSchemePermission && (
                <button
                  type="button"
                  onClick={() => navigate("create")}
                  className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-semibold text-white shadow-sm transition-colors"
                  style={{ backgroundColor: primaryColor["700"] || "#3b82f6" }}
                >
                  <FiPlus className="h-4 w-4" />
                  Add Scheme
                </button>
              )}
            </div>
          </div>

          <div className="p-4 space-y-3">

            {/* ── Filters ── */}
            <div className="bg-white rounded-lg border border-gray-200 shadow-sm px-4 py-3 flex flex-col sm:flex-row items-start sm:items-center gap-3 justify-between">
              {/* Search + date inputs */}
              <div className="flex flex-wrap gap-2 items-center">
                <div className="relative">
                  <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400 pointer-events-none" />
                  <input
                    type="text"
                    placeholder="Search by name…"
                    value={searchName}
                    onChange={(e) => setSearchName(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && handleSearch()}
                    className="pl-9 pr-3 py-1.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 w-52 placeholder:text-gray-400"
                  />
                </div>
                <div className="flex items-center gap-1.5">
                  <FiCalendar className="h-3.5 w-3.5 text-gray-400 shrink-0" />
                  <input
                    type="date"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                    className="py-1.5 px-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-gray-700"
                  />
                  <span className="text-gray-300 text-xs">→</span>
                  <input
                    type="date"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                    className="py-1.5 px-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-gray-700"
                  />
                </div>
                <button
                  type="button"
                  onClick={handleSearch}
                  className="px-3 py-1.5 rounded-lg text-xs font-semibold bg-gray-900 text-white hover:bg-gray-800 transition-colors"
                >
                  Search
                </button>
                {(searchName || startDate || endDate) && (
                  <button
                    type="button"
                    onClick={handleClearFilters}
                    className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs font-medium border border-gray-200 text-gray-500 hover:text-gray-700 hover:bg-gray-50 transition-colors"
                  >
                    <FiX className="h-3 w-3" />
                    Clear
                  </button>
                )}
              </div>

              {/* Status tabs — right side, mirroring RulesList layout */}
              <StatusTabs value={statusFilter} onChange={setStatusFilter} />
            </div>

            {/* ── Table ── */}
            <div className="bg-white rounded-lg border border-gray-200 shadow-sm">
              <DataTableComponent
                keyField={(row) => row.id}
                columns={columns}
                data={filteredData}
                loading={schemeIsLoading}
                totalRows={statusFilter === "All" ? totalRows : filteredData.length}
                handlePerRowsChange={handlePerRowsChange}
                handlePageChange={handlePageChange}
                paginationDefaultPage={currentPage}
                paginationResetDefaultPage={paginationResetToggle}
                conditionalRowStyles={[
                  {
                    when: (row) => row.id === activeMenuRowId,
                    style: { backgroundColor: "#f1f5f9" },
                  },
                ]}
              />
            </div>

          </div>
        </div>
      </div>

      {/* ── Modals ── */}
      {showModal && modalData && (
        <ViewSchemeModal
          show={showModal}
          setShow={setShowModal}
          scheme={modalData}
          giftCataloguesList={giftCataloguesList}
        />
      )}
      {showImageModal && (
        <ImageModal show setShow={setShowImageModal} size="lg" data={showImageModal}>
          <div className="aspect-h-1 aspect-w-1 w-full overflow-hidden rounded-lg bg-gray-200 xl:aspect-h-8 xl:aspect-w-7">
            <img src={showImageModal} className="h-full w-full object-cover object-center" alt="scheme" />
          </div>
        </ImageModal>
      )}
      {showBulkRewardModal && (
        <BulkRewardModal
          show={showBulkRewardModal}
          setShow={setShowBulkRewardModal}
          schemeData={selectedScheme}
        />
      )}
      {showBulkManualQrRewardModal && (
        <BulkManualQrRewardModal
          show={showBulkManualQrRewardModal}
          setShow={setShowBulkManualQrRewardModal}
          schemeData={selectedSchemeForQr}
        />
      )}
      {showAddSchemeUsersModal && (
        <AddSchemeUsersModal
          show={showAddSchemeUsersModal}
          setShow={setShowAddSchemeUsersModal}
          schemes={data}
        />
      )}
      {viewUsersScheme && (
        <ViewSchemeUsersModal
          show={!!viewUsersScheme}
          setShow={(open) => !open && setViewUsersScheme(null)}
          scheme={viewUsersScheme}
        />
      )}
    </>
  );
};

export default SchemeList;
